// SecureEmbed InnovateFlow Portal — Account Security (Fig66 reframe) + Encryption
// Key Management (v3 Tabbed Redesign underneath — User Story #1250).
//
// This page now leads with the personal security surface the mockup specifies
// (disclosure signing keypair, sessions, security posture, API keys) and keeps
// the pre-existing KMS "Data Encryption Keys" hierarchy manager reachable
// further down the same page — it is real, working functionality and stays.

import { useState, useEffect, useCallback } from 'react'
import {
  Key, RefreshCw, AlertTriangle, Check, X, Shield, Loader2, ShieldCheck,
  KeyRound, Monitor, LogOut, ChevronDown, ChevronUp,
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { STORAGE_KEYS } from '../../constants/config'
import KeyHierarchyTree from '../../components/encryption/KeyHierarchyTree'
import CustodyTierPanel from '../../components/encryption/CustodyTierPanel'
import ManageDevicesPanel from '../../components/disclosure/ManageDevicesPanel'
import ConfirmDialog from '../../components/shared/ConfirmDialog'
import PageHeader from '../../components/ui/PageHeader'
import ContextualTip from '../../components/ui/ContextualTip'
import {
  getDisclosureKeypair, listDisclosureKeypairs, registerDisclosureKeypair, getAuditLog,
} from '../../services/api'
import { generateKeypair, storeKeypair, exportPublicKeyHex, computeFingerprint } from '../../services/disclosureKeys'
import type { AuditEntry } from '../../types'

function formatDateTime(iso?: string): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-AU', {
      timeZone: 'Australia/Sydney',
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

const ACTIVITY_LABELS: Record<string, string> = {
  disclosure_keypair_registered: 'Signing key registered on a device',
  disclosure_keypair_revoked: 'Device signing key revoked',
}

function activityLabel(entry: AuditEntry): string {
  return ACTIVITY_LABELS[entry.action_type] || entry.action_type
}

/** Real, on-device signal — parsed from the browser's own User-Agent string.
 * No location/IP is fabricated; unsupported browsers/OSes show as "Unknown". */
function detectDevice(): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  let browser = 'Unknown browser'
  if (/Edg\//.test(ua)) browser = 'Edge'
  else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) browser = 'Chrome'
  else if (/Firefox\//.test(ua)) browser = 'Firefox'
  else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) browser = 'Safari'
  let os = 'Unknown OS'
  if (/Windows/.test(ua)) os = 'Windows'
  else if (/Mac OS X/.test(ua)) os = 'macOS'
  else if (/Android/.test(ua)) os = 'Android'
  else if (/iPhone|iPad/.test(ua)) os = 'iOS'
  else if (/Linux/.test(ua)) os = 'Linux'
  return `${browser} · ${os}`
}

function keyAgeStatus(registeredAt?: string): { label: string; tone: 'good' | 'consider' } {
  if (!registeredAt) return { label: 'No signing key yet', tone: 'consider' }
  const ms = Date.now() - new Date(registeredAt).getTime()
  const days = Math.max(0, Math.floor(ms / 86400000))
  if (days > 180) return { label: `Registered ${days}d ago — consider rotating`, tone: 'consider' }
  return { label: `Registered ${days}d ago`, tone: 'good' }
}

// Use runtime config API endpoint (from config.js generated by Terraform)
const API_BASE = (window as any).INNOVATEFLOW_CONFIG?.API_ENDPOINT || import.meta.env.VITE_API_BASE_URL || ''

interface CustomerKey {
  tenant_id: string
  key_type: string
  kms_key_arn: string
  key_source: 'customer-managed' | 'platform-managed'
  status: string
  created_at: string
  rotated_at?: string
  key_alias?: string
  key_description?: string
  scope_type?: 'org' | 'project' | 'folder' | 'document'
  scope_id?: string
  parent_key_id?: string
}

type KeyScope = 'org' | 'project' | 'folder' | 'document'

const SCOPE_INFO: Record<KeyScope, { title: string; description: string; icon: string; detail: string }> = {
  org: {
    title: 'Organisation',
    description: 'One key protects everything',
    icon: '🏢',
    detail: 'Best for most users — one key covers all your projects, folders, and documents.',
  },
  project: {
    title: 'Project',
    description: 'Independent security per project',
    icon: '📁',
    detail: 'Ideal when different projects need separate encryption keys for compliance or collaboration.',
  },
  folder: {
    title: 'Folder',
    description: 'Protect sensitive folders',
    icon: '📂',
    detail: 'For folders within a project that need their own encryption — e.g. confidential financial data.',
  },
  document: {
    title: 'Document',
    description: 'Individual document protection',
    icon: '📄',
    detail: 'Maximum granularity — encrypt a single document with its own key.',
  },
}

// Tab system removed — educational tabs removed along with the Security Help page

export default function EncryptionKeys() {
  const { user, logout } = useAuth()
  const activeOrgId = user?.active_org_id
  const token = localStorage.getItem(STORAGE_KEYS.ID_TOKEN) || ''

  // ── Disclosure signing keypair (personal security surface — Fig66) ──
  const [keypairStatus, setKeypairStatus] = useState<{ has_keypair: boolean; public_key_hex?: string; registered_at?: string } | null>(null)
  const [keypairLoading, setKeypairLoading] = useState(true)
  const [keypairError, setKeypairError] = useState('')
  const [fingerprint, setFingerprint] = useState('')
  const [deviceCount, setDeviceCount] = useState<number | null>(null)
  const [showRotateConfirm, setShowRotateConfirm] = useState(false)
  const [rotating, setRotating] = useState(false)
  const [rotateError, setRotateError] = useState('')
  const [showActivity, setShowActivity] = useState(false)
  const [activity, setActivity] = useState<AuditEntry[] | null>(null)
  const [activityLoading, setActivityLoading] = useState(false)
  const [activityError, setActivityError] = useState('')

  const refreshKeypairStatus = useCallback(async () => {
    setKeypairLoading(true)
    setKeypairError('')
    try {
      const status = await getDisclosureKeypair()
      setKeypairStatus(status)
      setFingerprint(status.public_key_hex ? await computeFingerprint(status.public_key_hex) : '')
    } catch (err: any) {
      setKeypairError(err.message || 'Failed to load your signing key status')
    } finally {
      setKeypairLoading(false)
    }
  }, [])

  const refreshDeviceCount = useCallback(async () => {
    try {
      const items = await listDisclosureKeypairs()
      setDeviceCount(items.length)
    } catch {
      setDeviceCount(null)
    }
  }, [])

  useEffect(() => { refreshKeypairStatus(); refreshDeviceCount() }, [refreshKeypairStatus, refreshDeviceCount])

  const loadActivity = useCallback(async () => {
    if (!activeOrgId) return
    setActivityLoading(true)
    setActivityError('')
    try {
      const { events } = await getAuditLog({ resource_id: activeOrgId, limit: 10 })
      setActivity(events)
    } catch (err: any) {
      setActivityError(err.message || 'Failed to load recent key activity')
    } finally {
      setActivityLoading(false)
    }
  }, [activeOrgId])

  const toggleActivity = async () => {
    const next = !showActivity
    setShowActivity(next)
    if (next && activity === null) await loadActivity()
  }

  const handleRotateKey = async () => {
    if (!activeOrgId) return
    setRotating(true)
    setRotateError('')
    try {
      const kp = await generateKeypair()
      const hex = await exportPublicKeyHex(kp.publicKey)
      await registerDisclosureKeypair(hex)
      await storeKeypair(kp, activeOrgId)
      setShowRotateConfirm(false)
      await Promise.all([refreshKeypairStatus(), refreshDeviceCount()])
      if (showActivity) {
        setActivity(null)
        await loadActivity()
      }
    } catch (err: any) {
      setRotateError(err.message || 'Failed to rotate your signing key')
    } finally {
      setRotating(false)
    }
  }

  // ── Active sessions — honest: we only know THIS session for real; no
  // device/session-tracking backend exists yet, so no other rows are invented ──
  const [thisDevice] = useState(() => detectDevice())
  const [showSignOutAllConfirm, setShowSignOutAllConfirm] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const [signOutError, setSignOutError] = useState('')

  const handleSignOutEverywhere = async () => {
    setSigningOut(true)
    setSignOutError('')
    try {
      const config = (window as { INNOVATEFLOW_CONFIG?: { REGION: string } }).INNOVATEFLOW_CONFIG
      const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || ''
      if (!config || !accessToken) throw new Error('Not signed in.')
      const resp = await fetch(`https://cognito-idp.${config.REGION}.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.GlobalSignOut',
        },
        body: JSON.stringify({ AccessToken: accessToken }),
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.message || 'Failed to sign out of all sessions.')
      setShowSignOutAllConfirm(false)
      logout()
    } catch (err: any) {
      setSignOutError(err.message || 'Failed to sign out of all sessions.')
    } finally {
      setSigningOut(false)
    }
  }

  const [keys, setKeys] = useState<CustomerKey[]>([])
  const [orgKeys, setOrgKeys] = useState<CustomerKey[]>([])
  const [projectKeys, setProjectKeys] = useState<CustomerKey[]>([])
  const [folderKeys, setFolderKeys] = useState<CustomerKey[]>([])
  const [documentKeys, setDocumentKeys] = useState<CustomerKey[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [actionLoading, setActionLoading] = useState('')
  // activeTab removed — only Manage Keys content remains

  // Form states
  const [registerArn, setRegisterArn] = useState('')
  const [migrateExisting, setMigrateExisting] = useState(false)

  // Wizard states
  const [showWizard, setShowWizard] = useState(false)
  const [wizardStep, setWizardStep] = useState<1 | 2 | 3>(1)
  const [keyChoice, setKeyChoice] = useState<'platform' | 'customer'>('platform')
  const [osTab, setOsTab] = useState<'win' | 'mac' | 'console'>('win')

  // Scope selection states (Story #1303 — hierarchical keys)
  const [selectedScope, setSelectedScope] = useState<KeyScope>('org')
  const [selectedScopeId, setSelectedScopeId] = useState('')
  const [showAdvancedScopes, setShowAdvancedScopes] = useState(false)
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([])
  // scope selector integrated directly into wizard Step 1

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }

  const api = async (path: string, opts?: RequestInit) => {
    const resp = await fetch(`${API_BASE}${path}`, { ...opts, headers: { ...headers, ...opts?.headers } })
    const ct = resp.headers.get('content-type') || ''
    if (ct.includes('text/html')) throw new Error('API endpoint not configured — this usually means deployment is still in progress.')
    if (resp.status === 401) throw new Error('Please sign in to manage encryption keys.')
    if (resp.status === 403) throw new Error('You don\'t have permission to manage encryption keys.')
    if (resp.status === 404) throw new Error('Encryption key service is not available yet — it will be configured during deployment.')
    if (resp.status >= 500) throw new Error('Something went wrong on our end. Please try again or contact support.')
    const data = await resp.json()
    if (!resp.ok) throw new Error(data.error || `Request failed: ${resp.status}`)
    return data
  }

  const fetchKeys = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const data = await api('/cmk/keys')
      setKeys(data.keys || [])
      setOrgKeys(data.org_keys || [])
      setProjectKeys(data.project_keys || [])
      setFolderKeys(data.folder_keys || [])
      setDocumentKeys(data.document_keys || [])
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchKeys() }, [fetchKeys])

  const createPlatformKey = async () => {
    try {
      setActionLoading('create')
      await api('/cmk/create-key', {
        method: 'POST',
        body: JSON.stringify({ migrate_existing: migrateExisting }),
      })
      setSuccess('Platform key created successfully')
      await fetchKeys()
    } catch (err: any) { setError(err.message) }
    finally { setActionLoading('') }
  }

  const revokeKey = async (keyArn: string) => {
    if (!window.confirm('Revoke this encryption key? Data encrypted with it will remain encrypted but new writes will use the platform default.')) return
    try {
      setActionLoading(`revoke-${keyArn}`)
      await api('/cmk/revoke-key', {
        method: 'POST',
        body: JSON.stringify({ kms_key_arn: keyArn }),
      })
      setSuccess('Key revoked')
      await fetchKeys()
    } catch (err: any) { setError(err.message) }
    finally { setActionLoading('') }
  }

  // tabs array removed — educational tabs removed along with the Security Help page

  // Wizard handlers
  const handleWizardFinish = async () => {
    try {
      const scopeParams = {
        scope_type: selectedScope,
        scope_id: selectedScope === 'org' ? undefined : selectedScopeId || undefined,
      }
      if (keyChoice === 'platform') {
        setActionLoading('create')
        await api('/cmk/create-key', { method: 'POST', body: JSON.stringify({ migrate_existing: migrateExisting, ...scopeParams }) })
        setSuccess(`Platform key created successfully${selectedScope !== 'org' ? ` at ${SCOPE_INFO[selectedScope].title} level` : ''}`)
      } else {
        if (!registerArn.trim()) return
        setActionLoading('register')
        await api('/cmk/register-key', { method: 'POST', body: JSON.stringify({ kms_key_arn: registerArn.trim(), migrate_existing: migrateExisting, ...scopeParams }) })
        setSuccess(`Key registered successfully${selectedScope !== 'org' ? ` at ${SCOPE_INFO[selectedScope].title} level` : ''}`)
      }
      setWizardStep(3)
      setRegisterArn('')
      await fetchKeys()
    } catch (err: any) { setError(err.message) }
    finally { setActionLoading('') }
  }

    const openWizard = () => { setShowWizard(true); setWizardStep(1); setKeyChoice('platform'); setRegisterArn(''); setMigrateExisting(false); setSelectedScope('org'); setSelectedScopeId(''); setShowAdvancedScopes(false) }

  // Fetch projects for scope picker
  const fetchProjects = async () => {
    try {
      const data = await api('/innovateflow/projects')
      setProjects((data.projects || []).map((p: any) => ({ id: p.project_id || p.id, name: p.name || p.project_name || p.id })))
    } catch { setProjects([]) }
  }
  const closeWizard = () => { setShowWizard(false); setWizardStep(1) }

  const keyAge = keyAgeStatus(keypairStatus?.registered_at)

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <PageHeader
        title="Security & Keys"
        description="Your disclosure signing key, sessions, and the AWS keys that encrypt your data. Every action here is real, with a confirmation before anything consequential."
        helpKey="account-security"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* ── Main column ── */}
        <div className="lg:col-span-2 space-y-4">
          {/* Disclosure Signing Keypair */}
          <div className="light-card p-5">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <ShieldCheck size={18} className="text-iflow-500" />
                <h2 className="text-base font-semibold text-gray-900">Disclosure Signing Key</h2>
              </div>
              {keypairStatus?.has_keypair && (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Active
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mb-4">
              This keypair signs your disclosures so counterparties can verify they genuinely came from
              you. Your private key never leaves this device.
            </p>

            {keypairLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                <Loader2 size={14} className="animate-spin" /> Loading…
              </div>
            ) : keypairError ? (
              <p className="text-sm text-red-600">{keypairError}</p>
            ) : keypairStatus?.has_keypair ? (
              <dl className="text-sm grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 mb-4">
                <div className="flex gap-2"><dt className="font-medium text-gray-700 w-24 shrink-0">Fingerprint</dt><dd className="font-mono text-xs text-gray-600 break-all">{fingerprint || '—'}</dd></div>
                <div className="flex gap-2"><dt className="font-medium text-gray-700 w-24 shrink-0">Algorithm</dt><dd className="text-gray-600">Ed25519 (signing)</dd></div>
                <div className="flex gap-2"><dt className="font-medium text-gray-700 w-24 shrink-0">Registered</dt><dd className="text-gray-600">{formatDateTime(keypairStatus.registered_at)}</dd></div>
                <div className="flex gap-2"><dt className="font-medium text-gray-700 w-24 shrink-0">Devices</dt><dd className="text-gray-600">{deviceCount ?? '—'} active</dd></div>
              </dl>
            ) : (
              <p className="text-sm text-gray-500 mb-4 italic">
                No signing key registered yet — one is created the first time you sign a disclosure, or you can
                register one now with Rotate key.
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowRotateConfirm(true)}
                disabled={!activeOrgId}
                title={!activeOrgId ? 'Switch to an organisation to manage your signing key' : undefined}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw size={14} /> Rotate key
              </button>
              <button
                onClick={toggleActivity}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                {showActivity ? <ChevronUp size={14} /> : <ChevronDown size={14} />} View recent activity
              </button>
            </div>

            {showRotateConfirm && (
              <div className="mt-4 border-l-4 border-amber-400 bg-amber-50 rounded-lg p-4">
                <p className="text-sm font-semibold text-amber-900 mb-1">Rotate your disclosure key?</p>
                <p className="text-sm text-amber-800 mb-3">
                  We&apos;ll generate a <b>new keypair</b> on this device. From then on, new disclosures are
                  signed with the new key. Your <b>old public key stays registered</b> so everything
                  you&apos;ve already signed remains verifiable — nothing is invalidated. You can revoke the
                  old device below once the new key is confirmed working.
                </p>
                {rotateError && <p className="text-sm text-red-700 mb-3">{rotateError}</p>}
                <div className="flex justify-end gap-2">
                  <button onClick={() => setShowRotateConfirm(false)} className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-white">Cancel</button>
                  <button
                    onClick={handleRotateKey}
                    disabled={rotating}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
                  >
                    {rotating && <Loader2 size={14} className="animate-spin" />} Rotate key
                  </button>
                </div>
              </div>
            )}

            {showActivity && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Recent Key Activity</h3>
                {activityLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
                ) : activityError ? (
                  <p className="text-sm text-red-600">{activityError}</p>
                ) : activity && activity.length > 0 ? (
                  <ul className="divide-y divide-gray-100">
                    {activity.map((e) => (
                      <li key={e.event_id} className="flex items-center justify-between py-2 text-sm">
                        <span className="text-gray-700">{activityLabel(e)}</span>
                        <span className="text-xs text-gray-400">{formatDateTime(e.timestamp)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 italic">No key activity recorded yet.</p>
                )}
              </div>
            )}
          </div>

          {/* Custody tier (four-tier CMK model) + WebAuthn step-up */}
          <CustodyTierPanel />

          {/* Registered signing devices — real, from listDisclosureKeypairs */}
          <div className="light-card p-5">
            {activeOrgId ? (
              <ManageDevicesPanel orgId={activeOrgId} />
            ) : (
              <p className="text-sm text-gray-500">No active organisation — signing devices are managed per-organisation.</p>
            )}
          </div>

          {/* API Keys — honest: no programmatic-API backend exists yet */}
          <div className="light-card p-5">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <KeyRound size={18} className="text-iflow-500" />
                <h2 className="text-base font-semibold text-gray-900">API Keys</h2>
              </div>
              <button
                disabled
                title="Programmatic API access is coming soon"
                className="px-3 py-1.5 text-sm text-gray-400 border border-gray-200 rounded-lg cursor-not-allowed"
              >
                + Create API key
              </button>
            </div>
            <p className="text-sm text-gray-500">
              Keys would authenticate programmatic access to the InnovateFlow API. There is no API-key
              service in this environment yet — this section is disabled rather than showing placeholder keys.
            </p>
          </div>
        </div>

        {/* ── Sidebar column ── */}
        <div className="space-y-4">
          {/* Active Sessions — honest: only this session is real; no
              cross-device session tracking backend exists yet */}
          <div className="light-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Monitor size={16} className="text-iflow-500" />
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Active Sessions</h2>
            </div>
            <div className="flex items-center justify-between text-sm mb-3">
              <span className="text-gray-800">{thisDevice}</span>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-iflow-100 text-iflow-700">This device</span>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              We don&apos;t yet track sign-ins from other devices, so no other rows are shown here — that
              tracking is on the roadmap rather than faked.
            </p>
            {signOutError && <p className="text-xs text-red-600 mb-2">{signOutError}</p>}
            <button
              onClick={() => setShowSignOutAllConfirm(true)}
              className="w-full px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 inline-flex items-center justify-center gap-1.5"
            >
              <LogOut size={14} /> Sign out of all sessions
            </button>
            <ConfirmDialog
              open={showSignOutAllConfirm}
              variant="danger"
              title="Sign out of all sessions?"
              message="This revokes every session for your account with our identity provider (Cognito) — including this device. You'll need to sign in again."
              confirmLabel={signingOut ? 'Signing out…' : 'Sign out everywhere'}
              onConfirm={handleSignOutEverywhere}
              onCancel={() => setShowSignOutAllConfirm(false)}
            />
          </div>

          {/* Security posture — real, computed from live signals */}
          <div className="light-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Shield size={16} className="text-iflow-500" />
              <h2 className="text-sm font-semibold text-gray-900">Security posture</h2>
            </div>
            <ul className="space-y-2.5 text-sm">
              <li className="flex items-center justify-between">
                <span className="text-gray-700">Disclosure signing key</span>
                {keypairStatus?.has_keypair ? (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Active</span>
                ) : (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Missing</span>
                )}
              </li>
              <li className="flex items-center justify-between">
                <span className="text-gray-700">Registered devices</span>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-iflow-100 text-iflow-700">{deviceCount ?? '—'}</span>
              </li>
              <li className="flex items-center justify-between gap-2">
                <span className="text-gray-700">{keyAge.label}</span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${keyAge.tone === 'good' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {keyAge.tone === 'good' ? 'Healthy' : 'Consider'}
                </span>
              </li>
              <li className="flex items-center justify-between gap-2">
                <span className="text-gray-700">Two-factor authentication</span>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500" title="Not surfaced by this screen yet — managed by your identity provider">
                  Not shown here
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Alerts (CMK section) */}
      {error && (
        <div className="bg-red-500/10 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-800 flex-1">{error}</p>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X size={16} /></button>
        </div>
      )}
      {success && (
        <div className="bg-green-500/10 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <Check size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-green-800 flex-1">{success}</p>
          <button onClick={() => setSuccess('')} className="text-green-400 hover:text-green-600"><X size={16} /></button>
        </div>
      )}

      {/* Data Encryption Keys (KMS) — pre-existing, unchanged functionality.
          Distinct from the signing key above: these protect documents at rest;
          the signing key above authenticates your disclosures. */}
      <div className="pt-2">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-900">Data Encryption Keys (KMS)</h2>
          <button
            onClick={() => fetchKeys()}
            disabled={loading}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-iflow-700 border border-iflow-500 rounded-lg hover:bg-iflow-50 disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          Protect your data with encryption — you choose who holds the key. Holding a key is the
          prerequisite for receiving disclosures.
        </p>

        <ContextualTip id="account-security-keys">
          A <b>platform-managed</b> key is created and rotated for you — one click, no AWS account. A{' '}
          <b>customer-managed</b> key means you bring your own AWS KMS key and keep full control for
          compliance. You can start with platform-managed and add your own key later.
        </ContextualTip>
      </div>

      {/* Key Management Content */}
      <div className="space-y-6">
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <div className="group relative">
              <button
                onClick={() => createPlatformKey()}
                disabled={actionLoading === 'create'}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {actionLoading === 'create' ? <Loader2 size={14} className="animate-spin" /> : <Shield size={14} />} Create Platform Key
              </button>
              <div className="invisible group-hover:visible absolute bottom-full left-0 mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg z-10">
                We create a dedicated key in our secure vault. Auto-rotation and backups included. No AWS account required.
              </div>
            </div>
            <div className="group relative">
              <button
                onClick={openWizard}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <Key size={14} /> Register Your Key
              </button>
              <div className="invisible group-hover:visible absolute bottom-full left-0 mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg z-10">
                Connect a KMS key from your own AWS account. You&apos;ll need the key&apos;s ARN. Full control stays with you.
              </div>
            </div>
            {/* BUG-122: these had no onClick and no backing endpoint — the
                /cmk API exposes only keys/create-key/register-key/revoke-key/
                effective-key (no rotate, no audit). Rather than fake an action,
                disable them honestly until the backend exists. */}
            <div className="group relative">
              <button
                disabled
                aria-disabled="true"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-gray-300 text-gray-400 rounded-lg cursor-not-allowed"
              >
                <RefreshCw size={14} /> Rotate Key
              </button>
              <div className="invisible group-hover:visible absolute bottom-full left-0 mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg z-10">
                Manual key rotation is coming soon.
              </div>
            </div>
            <div className="group relative">
              <button
                disabled
                aria-disabled="true"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-gray-300 text-gray-400 rounded-lg cursor-not-allowed"
              >
                View Audit Log
              </button>
              <div className="invisible group-hover:visible absolute bottom-full left-0 mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg z-10">
                A dedicated encryption audit log is coming soon.
              </div>
            </div>
          </div>

          {/* Key Hierarchy Tree — primary key display (Story #1347) */}
          <div className="light-card shadow-sm p-5">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-base font-semibold text-gray-900">Key Hierarchy</h3>
              <span className="text-xs text-gray-400">{keys.length} key{keys.length !== 1 ? 's' : ''}</span>
            </div>
            <p className="text-xs text-gray-500 mb-4">See which keys protect your projects, folders, and documents. Keys inherit from parent levels unless overridden.</p>
            <KeyHierarchyTree
              orgKeys={orgKeys}
              projectKeys={projectKeys}
              folderKeys={folderKeys}
              documentKeys={documentKeys}
              onSetupKey={openWizard}
              onRevoke={(key) => revokeKey(key.kms_key_arn)}
              loading={loading}
              error={!!error}
            />
          </div>
        </div>

      {/* Wizard Modal */}
      {showWizard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={e => { if (e.target === e.currentTarget) closeWizard() }}>
          <div className="bg-white rounded-2xl border border-iflow-100 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Wizard Header */}
            <div className="bg-gradient-to-r from-iflow-600 to-iflow-800 rounded-t-2xl px-6 py-5 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Encryption Setup Wizard</h2>
                  <p className="text-iflow-200 text-sm mt-1">We&apos;ll help you choose and set up encryption</p>
                </div>
                <button onClick={closeWizard} className="text-white/60 hover:text-white text-2xl">&times;</button>
              </div>
              <div className="flex items-center gap-2 mt-5">
                {[{ n: 1, label: 'Choose' }, { n: 2, label: 'Set Up' }, { n: 3, label: 'Done' }].map((s, i) => (
                  <span key={s.n} className="contents">
                    {i > 0 && <div className="flex-1 h-0.5 bg-white/30 mx-1" />}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${wizardStep >= s.n ? 'bg-white text-iflow-700' : 'bg-white/20 text-white/60'}`}>{s.n}</div>
                    <span className={`text-sm ${wizardStep >= s.n ? 'font-medium text-white' : 'text-iflow-300'}`}>{s.label}</span>
                  </span>
                ))}
              </div>
            </div>

            {/* Step 1: Choose */}
            {wizardStep === 1 && (
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-1">Which option is right for you?</h3>
                <p className="text-sm text-gray-500 mb-5">Pick the approach that matches your needs. You can always change later.</p>
                <div className="space-y-3">
                  <label className={`flex items-start gap-3 border-2 rounded-xl p-4 cursor-pointer transition-colors ${keyChoice === 'platform' ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 hover:border-emerald-400'}`}>
                    <input type="radio" name="key-choice" checked={keyChoice === 'platform'} onChange={() => setKeyChoice('platform')} className="mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900"><Shield size={14} className="inline mr-1" />Platform-Managed Key</p>
                      <p className="text-sm text-gray-500 mt-0.5">We handle everything. One-click setup, no AWS account needed.</p>
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 border-2 rounded-xl p-4 cursor-pointer transition-colors ${keyChoice === 'customer' ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 hover:border-indigo-400'}`}>
                    <input type="radio" name="key-choice" checked={keyChoice === 'customer'} onChange={() => setKeyChoice('customer')} className="mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900"><Key size={14} className="inline mr-1" />Customer-Managed Key</p>
                      <p className="text-sm text-gray-500 mt-0.5">Bring your own AWS KMS key. Full control, meets compliance requirements.</p>
                    </div>
                  </label>
                </div>
                {/* Encryption Level / Scope Selector (Story #1303) */}
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-700 mb-1">What should this key protect?</h4>
                  <p className="text-xs text-gray-400 mb-3">Most users choose Organisation level. You can always add more granular keys later.</p>
                  <div className="grid grid-cols-2 gap-2">
                    {(['org', 'project'] as const).map(scope => (
                      <button key={scope} onClick={() => { setSelectedScope(scope); setSelectedScopeId(''); if (scope !== 'org') fetchProjects() }}
                        className={`relative text-left p-3 rounded-lg border-2 transition-colors ${selectedScope === scope ? 'border-iflow-500 bg-iflow-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <span className="text-lg">{SCOPE_INFO[scope].icon}</span>
                        <p className="text-sm font-medium text-gray-900 mt-1">{SCOPE_INFO[scope].title}</p>
                        <p className="text-xs text-gray-500">{SCOPE_INFO[scope].description}</p>
                        {scope === 'org' && <span className="absolute top-2 right-2 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium">Recommended</span>}
                      </button>
                    ))}
                  </div>
                  {!showAdvancedScopes && (
                    <button onClick={() => setShowAdvancedScopes(true)} className="text-xs text-iflow-600 hover:text-iflow-700 mt-2 font-medium">
                      More options (Folder, Document) →
                    </button>
                  )}
                  {showAdvancedScopes && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {(['folder', 'document'] as const).map(scope => (
                        <button key={scope} onClick={() => { setSelectedScope(scope); setSelectedScopeId('') }}
                          className={`text-left p-3 rounded-lg border-2 transition-colors ${selectedScope === scope ? 'border-iflow-500 bg-iflow-50' : 'border-gray-200 hover:border-gray-300'}`}>
                          <span className="text-lg">{SCOPE_INFO[scope].icon}</span>
                          <p className="text-sm font-medium text-gray-900 mt-1">{SCOPE_INFO[scope].title}</p>
                          <p className="text-xs text-gray-500">{SCOPE_INFO[scope].description}</p>
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Resource picker for non-org scopes */}
                  {selectedScope !== 'org' && (
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Select {SCOPE_INFO[selectedScope].title}
                      </label>
                      {selectedScope === 'project' && projects.length > 0 ? (
                        <select value={selectedScopeId} onChange={e => setSelectedScopeId(e.target.value)}
                          className="w-full light-input px-3 py-2 text-sm">
                          <option value="">Choose a project...</option>
                          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      ) : (
                        <input type="text" value={selectedScopeId} onChange={e => setSelectedScopeId(e.target.value)}
                          placeholder={`Enter ${SCOPE_INFO[selectedScope].title.toLowerCase()} ID...`}
                          className="w-full light-input px-3 py-2 text-sm" />
                      )}
                    </div>
                  )}
                  {/* Consequences preview */}
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
                    {selectedScope === 'org' && '🔒 Your key will protect all projects, folders, and documents in your account.'}
                    {selectedScope === 'project' && (selectedScopeId
                      ? `🔒 Your key will protect the selected project and everything inside it. Other projects remain on your organisation key.`
                      : 'Select a project above to see what will be protected.')}
                    {selectedScope === 'folder' && '🔒 Your key will protect this folder and its documents. Other folders remain on the project key.'}
                    {selectedScope === 'document' && '🔒 Your key will protect only this document. Other documents remain on the parent key.'}
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button onClick={() => keyChoice === 'platform' ? handleWizardFinish() : setWizardStep(2)}
                    disabled={!!actionLoading || (selectedScope !== 'org' && !selectedScopeId)}
                    className="px-6 py-2.5 bg-iflow-600 text-white rounded-lg hover:bg-iflow-700 font-medium text-sm disabled:opacity-50">
                    {keyChoice === 'platform' ? (actionLoading ? 'Creating...' : 'Create Platform Key') : 'Next: Set Up →'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Set Up (Customer key) */}
            {wizardStep === 2 && (
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-1">Set Up Your Key</h3>
                <p className="text-sm text-gray-500 mb-5">Follow the instructions for your platform, then paste the key identifier below.</p>
                <div className="flex border-b border-gray-200 mb-4 overflow-x-auto">
                  {([['win', 'Windows PowerShell'], ['mac', 'macOS Terminal'], ['console', 'AWS Console']] as const).map(([id, label]) => (
                    <button key={id} onClick={() => setOsTab(id)} className={`px-4 py-2 text-sm whitespace-nowrap border-b-2 ${osTab === id ? 'border-iflow-500 text-iflow-700 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                      {label}
                    </button>
                  ))}
                </div>

                {osTab === 'win' && (
                  <div>
                    <div className="bg-gray-900 rounded-lg p-4 mb-4 overflow-x-auto">
                      <div className="flex items-center gap-2 mb-2"><div className="w-3 h-3 bg-red-500 rounded-full" /><div className="w-3 h-3 bg-yellow-500 rounded-full" /><div className="w-3 h-3 bg-green-500 rounded-full" /><span className="text-gray-400 text-xs ml-2">PowerShell</span></div>
                      <pre className="text-green-400 text-xs leading-relaxed"><code>{`# Step 1: Install AWS CLI (skip if already installed)\nwinget install Amazon.AWSCLI\n\n# Step 2: Set up your credentials\naws configure\n\n# Step 3: Create a KMS key\naws kms create-key \`\n  --description "SecureEmbed encryption key" \`\n  --key-usage ENCRYPT_DECRYPT \`\n  --region ap-southeast-2\n\n# Step 4: Copy the "Arn" value from the output`}</code></pre>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4"><p className="text-xs text-blue-800"><strong>Tip:</strong> Press <kbd className="bg-gray-100 px-1 py-0.5 rounded border border-gray-300 text-gray-700 text-[10px]">Win + X</kbd> then select &quot;Windows PowerShell&quot; to open the terminal.</p></div>
                  </div>
                )}

                {osTab === 'mac' && (
                  <div>
                    <div className="bg-gray-900 rounded-lg p-4 mb-4 overflow-x-auto">
                      <div className="flex items-center gap-2 mb-2"><div className="w-3 h-3 bg-red-500 rounded-full" /><div className="w-3 h-3 bg-yellow-500 rounded-full" /><div className="w-3 h-3 bg-green-500 rounded-full" /><span className="text-gray-400 text-xs ml-2">Terminal</span></div>
                      <pre className="text-green-400 text-xs leading-relaxed"><code>{`# Step 1: Install AWS CLI (skip if already installed)\nbrew install awscli\n\n# Step 2: Set up your credentials\naws configure\n\n# Step 3: Create a KMS key\naws kms create-key \\\n  --description "SecureEmbed encryption key" \\\n  --key-usage ENCRYPT_DECRYPT \\\n  --region ap-southeast-2\n\n# Step 4: Copy the "Arn" value from the output`}</code></pre>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4"><p className="text-xs text-blue-800"><strong>Tip:</strong> Press <kbd className="bg-gray-100 px-1 py-0.5 rounded border border-gray-300 text-gray-700 text-[10px]">Cmd + Space</kbd>, type &quot;Terminal&quot;, and press Enter.</p></div>
                  </div>
                )}

                {osTab === 'console' && (
                  <ol className="space-y-3 mb-4">
                    {[
                      { title: 'Open the AWS KMS Console', desc: 'Sign in to your AWS account and navigate to Key Management Service' },
                      { title: 'Click "Create key"', desc: 'Select "Symmetric" key type and "Encrypt and decrypt" usage' },
                      { title: 'Name your key', desc: 'Use an alias like "SecureEmbed-encryption" and add a description' },
                      { title: 'Add SecureEmbed as a key user', desc: 'Add external account 768693789269 with GenerateDataKey, Decrypt, and DescribeKey permissions' },
                      { title: 'Copy the Key ARN', desc: 'After creation, copy the full ARN from the key details page' },
                    ].map((step, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-iflow-100 text-iflow-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{step.title}</p>
                          <p className="text-xs text-gray-500">{step.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}

                <div className="border-t border-gray-100 pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Paste your Key ARN here</label>
                  <input
                    type="text"
                    value={registerArn}
                    onChange={e => setRegisterArn(e.target.value)}
                    placeholder="arn:aws:kms:ap-southeast-2:123456789012:key/..."
                    className="w-full px-3 py-2.5 light-input text-sm font-mono mb-3"
                  />
                  <label className="flex items-center gap-2 mb-4">
                    <input type="checkbox" checked={migrateExisting} onChange={e => setMigrateExisting(e.target.checked)} className="h-4 w-4 text-iflow-600 rounded" />
                    <span className="text-sm text-gray-700">Re-encrypt my existing data with this key</span>
                  </label>
                  <div className="flex items-center justify-between">
                    <button onClick={() => setWizardStep(1)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">← Back</button>
                    <button
                      onClick={handleWizardFinish}
                      disabled={!registerArn.trim() || !!actionLoading}
                      className="px-6 py-2.5 bg-iflow-600 text-white rounded-lg hover:bg-iflow-700 font-medium text-sm disabled:opacity-50"
                    >
                      {actionLoading ? 'Registering...' : 'Register Key & Finish →'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Done */}
            {wizardStep === 3 && (
              <div className="p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center text-3xl mx-auto mb-4">
                  <Check size={32} className="text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">You&apos;re All Set!</h3>
                <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">Your encryption key has been registered and is now protecting your data. All new records will be encrypted with this key.</p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-left max-w-sm mx-auto">
                  <div className="text-sm text-green-700 space-y-1.5">
                    <div className="flex items-center gap-2"><Check size={14} className="text-green-600" /> <span>Key registered successfully</span></div>
                    <div className="flex items-center gap-2"><Check size={14} className="text-green-600" /> <span>Encryption active</span></div>
                    <div className="flex items-center gap-2"><RefreshCw size={14} className="text-green-600 animate-spin" /> <span>Background re-encryption in progress...</span></div>
                  </div>
                </div>
                <button onClick={closeWizard} className="px-6 py-2.5 bg-iflow-600 text-white rounded-lg hover:bg-iflow-700 font-medium text-sm">Close Wizard</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
